import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Spinner } from './components/Spinner';

// After a hosting deploy the live `index.html` references freshly hashed
// asset chunks; any tab still running the previous build will dynamic-import
// chunk filenames that no longer exist, surfacing as "Failed to fetch
// dynamically imported module". Detect that specific failure once and force
// a hard reload to pick up the new index.html — but only once per session,
// so a genuinely broken chunk doesn't loop.
function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? '';
      const looksLikeStaleChunk = /dynamically imported module|importing a module script/i.test(msg);
      const sentinel = 'cnapractice_stale_chunk_reloaded';
      if (looksLikeStaleChunk && !sessionStorage.getItem(sentinel)) {
        sessionStorage.setItem(sentinel, '1');
        window.location.reload();
        // Block this resolution; the reload tears down the page.
        return await new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// Eager-loaded — these are on the highest-traffic paths (landing → trial)
// or are tiny + universally-needed (auth pages). Splitting them off would
// add a roundtrip before first paint.
import { LandingPage } from './pages/LandingPage';
import { BlogIndex } from './pages/BlogIndex';
import { BlogPost } from './pages/BlogPost';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { VerifyEmail } from './pages/VerifyEmail';
import { TrialStart } from './pages/TrialStart';
import { TrialExam } from './pages/TrialExam';
import { TrialResults } from './pages/TrialResults';
import { Onboarding } from './pages/Onboarding';
import { AdminVerify } from './pages/AdminVerify';
import { PrivacyPolicy } from './pages/PrivacyPolicy';

// Lazy-loaded — protected app surfaces. Bundle splitter creates a separate
// chunk per route, fetched on first navigation. Keeps initial load lean for
// the typical visitor (who lands → trials, never sees these).
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const ExamModeSelect = lazyWithRetry(() =>
  import('./pages/ExamModeSelect').then((m) => ({ default: m.ExamModeSelect })),
);
const Exam = lazyWithRetry(() => import('./pages/Exam').then((m) => ({ default: m.Exam })));
const Review = lazyWithRetry(() => import('./pages/Review').then((m) => ({ default: m.Review })));
// Admin shell + panes — the bespoke four-engine instrument at /admin.
// (Legacy 4,378-line Admin.tsx was deleted at the clean-start cutover.)
const AdminShell = lazyWithRetry(() => import('./admin/shell/AdminShell').then((m) => ({ default: m.AdminShell })));
const PulsePane = lazyWithRetry(() => import('./admin/panes/Pulse').then((m) => ({ default: m.PulsePane })));
const ContentBankPane = lazyWithRetry(() => import('./admin/panes/ContentBankSwarm').then((m) => ({ default: m.ContentBankPane })));
const BrainPane = lazyWithRetry(() => import('./admin/panes/BrainConstellation').then((m) => ({ default: m.BrainPane })));
const QuestionSandboxPane = lazyWithRetry(() => import('./admin/panes/QuestionSandbox').then((m) => ({ default: m.QuestionSandboxPane })));
const GeneratorPane = lazyWithRetry(() => import('./admin/panes/GeneratorForge').then((m) => ({ default: m.GeneratorPane })));
const AssemblyPane = lazyWithRetry(() => import('./admin/panes/AssemblyLoom').then((m) => ({ default: m.AssemblyPane })));
const VersionsPane = lazyWithRetry(() => import('./admin/panes/VersionsTimeline').then((m) => ({ default: m.VersionsPane })));
const CostPane = lazyWithRetry(() => import('./admin/panes/CostTelemetry').then((m) => ({ default: m.CostPane })));
// SettingsPane stub deleted; SettingsPageFull replaces it.
const ProvenanceWalkerPane = lazyWithRetry(() =>
  import('./admin/panes/ProvenanceWalker').then((m) => ({ default: m.ProvenanceWalkerPane })),
);
const ExamReplayPane = lazyWithRetry(() => import('./admin/panes/ExamReplay').then((m) => ({ default: m.ExamReplayPane })));
const SourceDetailPane = lazyWithRetry(() => import('./admin/panes/SourceDetail').then((m) => ({ default: m.SourceDetailPane })));
const ReviewQueuePane = lazyWithRetry(() => import('./admin/panes/ReviewQueue').then((m) => ({ default: m.ReviewQueuePane })));
const SettingsPaneFull = lazyWithRetry(() => import('./admin/panes/SettingsPage').then((m) => ({ default: m.SettingsPane })));
const StorageHealthPane = lazyWithRetry(() => import('./admin/panes/StorageHealth').then((m) => ({ default: m.StorageHealthPane })));
const UsersPane = lazyWithRetry(() => import('./admin/panes/Users').then((m) => ({ default: m.UsersPane })));
const CouponsPane = lazyWithRetry(() => import('./admin/panes/Coupons').then((m) => ({ default: m.CouponsPane })));
const BlogPane = lazyWithRetry(() => import('./admin/panes/Blog').then((m) => ({ default: m.BlogPane })));
const Account = lazyWithRetry(() => import('./pages/Account').then((m) => ({ default: m.Account })));
const StartingCheck = lazyWithRetry(() => import('./pages/StartingCheck').then((m) => ({ default: m.StartingCheck })));
const AppLayout = lazyWithRetry(() =>
  import('./components/Layout').then((m) => ({ default: m.AppLayout })),
);

function FullPageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner className="h-8 w-8 text-brand-600" />
    </div>
  );
}

// Wrap a lazy element so its Suspense fallback is full-page. Keeps the per-
// route element declarations clean.
function L({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageLoading />}>{children}</Suspense>;
}

// "/" dispatcher: signed-in registered+verified users go to dashboard (or
// onboarding if not yet onboarded); signed-out and anonymous users see the
// public landing page.
function RootGate() {
  const { user, isAnonymous, profile, loading, needsEmailVerification } = useAuth();
  if (loading) return <FullPageLoading />;
  if (user && !isAnonymous && !needsEmailVerification) {
    if (profile && !profile.onboardingComplete) {
      return <Navigate to="/onboarding" replace />;
    }
    if (profile?.diagnosticRequired && profile.diagnosticStatus !== 'completed') return <Navigate to="/starting-check" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

function ProtectedGate() {
  const { user, isAnonymous, loading, needsEmailVerification, profile, profileComplete } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading />;
  if (!user || isAnonymous) return <Navigate to="/signin" replace />;
  if (needsEmailVerification) return <Navigate to="/verify-email" replace />;
  // Fresh users who never completed the welcome flow → onboarding.
  if (profile && !profile.onboardingComplete) return <Navigate to="/onboarding" replace />;
  if (profile?.diagnosticRequired && profile.diagnosticStatus !== 'completed' && location.pathname !== '/starting-check' && location.pathname !== '/account') return <Navigate to="/starting-check" replace />;
  // Legacy / partially-complete users (onboardingComplete=true but missing a
  // required field like phone): pin them to the settings page (/account)
  // until they save the missing fields. /account is whitelisted so they have
  // somewhere to actually fill the form.
  if (profile && !profileComplete && location.pathname !== '/account') {
    return <Navigate to="/account" replace />;
  }
  return <Outlet />;
}

function OnboardingGate() {
  const { user, isAnonymous, loading, needsEmailVerification, profile } = useAuth();
  if (loading) return <FullPageLoading />;
  if (!user || isAnonymous) return <Navigate to="/signin" replace />;
  if (needsEmailVerification) return <Navigate to="/verify-email" replace />;
  if (profile?.onboardingComplete) return <Navigate to={profile.diagnosticRequired && profile.diagnosticStatus !== 'completed' ? '/starting-check' : '/dashboard'} replace />;
  return <Outlet />;
}

function AdminGate() {
  const { isAdmin, profileIsAdmin, tokenIsAdmin, adminClaimLoading, loading } = useAuth();
  if (loading || adminClaimLoading || tokenIsAdmin === null) return <FullPageLoading />;
  if (!isAdmin) return <Navigate to={profileIsAdmin ? '/admin/verify' : '/dashboard'} replace />;
  return <Outlet />;
}

function PublicOnlyGate() {
  const { user, isAnonymous, loading } = useAuth();
  if (loading) return <FullPageLoading />;
  if (user && !isAnonymous) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  // Root dispatcher
  { path: '/', element: <RootGate /> },

  // Public auth surfaces
  {
    element: <PublicOnlyGate />,
    children: [
      { path: '/signin', element: <SignIn /> },
      { path: '/signup', element: <SignUp /> },
    ],
  },
  { path: '/verify-email', element: <VerifyEmail /> },
  { path: '/privacy', element: <PrivacyPolicy /> },

  // Onboarding (authed but not yet onboarded)
  {
    element: <OnboardingGate />,
    children: [{ path: '/onboarding', element: <Onboarding /> }],
  },

  // Trial — open to everyone (signs anonymous users in automatically)
  { path: '/trial/new', element: <TrialStart /> },
  { path: '/trial/:sessionId', element: <TrialExam /> },
  { path: '/trial/:sessionId/results', element: <TrialResults /> },

  // Public blog — fully crawlable, no auth gate.
  { path: '/blog', element: <L><BlogIndex /></L> },
  { path: '/blog/:slug', element: <L><BlogPost /></L> },

  // Admin diagnostic — anyone signed in can view their own status (it's a
  // self-diagnostic, not gated by admin role itself)
  { path: '/admin/verify', element: <AdminVerify /> },

  // Protected app — requires registered + verified + onboarded
  {
    element: <ProtectedGate />,
    children: [
      // Learner app routes — wrapped in AppLayout (top header + bottom nav).
      {
        element: (
          <L>
            <AppLayout />
          </L>
        ),
        children: [
          { path: '/starting-check', element: <L><StartingCheck /></L> },
          { path: '/dashboard', element: <L><Dashboard /></L> },
          { path: '/exam/new', element: <L><ExamModeSelect /></L> },
          { path: '/exam/:sessionId', element: <L><Exam /></L> },
          { path: '/exam/:sessionId/review', element: <L><Review /></L> },
          { path: '/account', element: <L><Account /></L> },
        ],
      },
      // Admin shell — runs OUTSIDE AppLayout so the learner header doesn't
      // double-stack on top of the admin sidebar.
      {
        element: <AdminGate />,
        children: [
          {
            path: '/admin',
            element: <L><AdminShell /></L>,
            children: [
              { index: true, element: <Navigate to="content" replace /> },
              { path: 'pulse',      element: <L><PulsePane /></L> },
              { path: 'content',    element: <L><ContentBankPane /></L> },
              { path: 'content/:sourceId', element: <L><SourceDetailPane /></L> },
              { path: 'brain',      element: <L><BrainPane /></L> },
              { path: 'generator',  element: <L><GeneratorPane /></L> },
              { path: 'generator/review', element: <L><ReviewQueuePane /></L> },
              { path: 'generator/sandbox', element: <L><QuestionSandboxPane /></L> },
              { path: 'exams',      element: <L><AssemblyPane /></L> },
              { path: 'exams/replay/:examId', element: <L><ExamReplayPane /></L> },
              { path: 'provenance', element: <L><ProvenanceWalkerPane /></L> },
              { path: 'versions',   element: <L><VersionsPane /></L> },
              { path: 'cost',       element: <L><CostPane /></L> },
              { path: 'storage',    element: <L><StorageHealthPane /></L> },
              { path: 'users',      element: <L><UsersPane /></L> },
              { path: 'coupons',    element: <L><CouponsPane /></L> },
              { path: 'blog',       element: <L><BlogPane /></L> },
              { path: 'settings',   element: <L><SettingsPaneFull /></L> },
            ],
          },
          { path: '/admin/v2/*', element: <Navigate to="/admin" replace /> },
        ],
      },
    ],
  },

  // Catch-all → root dispatcher
  { path: '*', element: <Navigate to="/" replace /> },
]);
